import { forwardRef } from 'react';
import { IconButton, Box, Typography, useTheme, Dialog, Tooltip } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { CustomWaterLoading, MenuPopover } from 'src/components';
import { MenuPopoverItem } from './MenuPopoverItem';
import { TrendModalContent } from './TrendModalContent/TrendModalContent';
import { card, cardHeader, cardTitle, cardContext, wrapperIconsBox, spinnerBox } from './style';
import { ModalRemoveContent } from './ModalRemoveContent';
import { WidgetService } from 'src/services/widgets/widgets.service';
import { useAuthentication } from 'src/features/authentication/context';
import { AccessRoles } from 'src/features/user-account-details/types';
import { ExpandIcon } from 'src/assets/icons/ExpandIcon';
import { AlarmPriority } from 'src/types/alarms';
import { HandlePriority } from './HandlePriority/HandlePriority';
import { LastUpdatedBox } from './LastUpdatedBox';
import { useToggle } from 'src/hooks/useToggle';
import { useResponsive } from 'src/hooks/useResponsive';
import { useHtmlElement } from 'src/hooks/useHtmlElement';
import { CloseIcon } from 'src/assets/icons/CloseIcon';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { EditWidgetIcon } from 'src/assets/icons/EditWidgetIcon';
import { MoreVerticalFillIcon } from 'src/assets/icons/MoreVerticalFillIcon';

type Props = {
  children: React.ReactNode;
  title: string;
  width?: number;
  height?: number;
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  isDataReady: boolean;
  isTrendWidget?: boolean;
  options?: ApexOptions;
  series?: any;
  typeChart?: 'area' | 'bar';
  unit?: string;
  isPumpWidget?: boolean;
  isControllerWidget?: boolean;
  noDataAvailable?: boolean;
  isValveWidget?: boolean;
  isWetWell?: boolean;
  assetPriority?: AlarmPriority;
  isLiveDataWidget?: boolean;
  setIsLiveDataModalOpen?: (isOpen: boolean) => void;
  timestamp?: string | null;
  isAlarmForPumpActive?: boolean;
  isLiftStationWidget?: boolean;
  onEdit?: () => void;
  isPumpMappedToAlarmTags?: boolean;
  isAssetController?: boolean;
};

export const WidgetWrapper = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      title,
      width,
      height,
      isEditMode,
      widgetId,
      toggleAddOrRemoveHandler,
      testId,
      isDataReady = false,
      isTrendWidget = false,
      isPumpWidget = false,
      options,
      series,
      typeChart,
      unit,
      isControllerWidget = false,
      isValveWidget = false,
      noDataAvailable,
      isLiveDataWidget = false,
      setIsLiveDataModalOpen,
      isWetWell,
      timestamp,
      isAlarmForPumpActive,
      isLiftStationWidget,
      onEdit,
      isPumpMappedToAlarmTags,
      isAssetController,
    }: Props,
    ref
  ) => {
    const theme = useTheme();
    const greyColor = theme.palette.grey[500];
    const redColor = theme.palette.error.main;

    const { htmlElement, addHtmlElement, removeHtmlElement } = useHtmlElement();
    const isDesktop = useResponsive('up', 'lg');
    const isTablet = useResponsive('up', 'sm');
    const { state, onToggle } = useToggle();
    const { state: isTrendDialogOpen, onToggle: onTrendDialogToggle } = useToggle();
    const {
      customerId: { value: customerId },
      siteId: { value: siteId },
      getCurrentRole,
    } = useAuthentication();

    const onRemoveHandler = () => {
      onToggle();
    };

    const onEditHandler = () => {
      if (setIsLiveDataModalOpen) setIsLiveDataModalOpen(true);
      if (onEdit) onEdit();
    };

    const onCloseDialog = () => {
      if (state) onToggle();
    };

    const onCloseTrendDialog = () => {
      if (isTrendDialogOpen) onTrendDialogToggle();
    };

    const removeWidgetHandler = async () => {
      if (!customerId || !siteId || !widgetId) return;
      try {
        await WidgetService.remove({ customerId: customerId, siteId: siteId, widgetId: widgetId });
        if (toggleAddOrRemoveHandler) toggleAddOrRemoveHandler();
        onCloseDialog();
      } catch (error) {
        console.error(error);
      }
    };

    function isOverflown(element: any) {
      return (
        element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth
      );
    }

    const els = document.getElementsByClassName('widget-wrapper-title');

    for (var i = 0; i < els.length; i++) {
      const el = els[i];
      const content = els[i]?.textContent ?? '';
      const contentLength = content.length;

      if (el) {
        el.textContent = '';
        for (var j = 0; j < contentLength; j++) {
          el.textContent = content.substring(0, j + 1);

          if (isOverflown(el)) {
            el.textContent = content.substring(0, j - 3);
            el.textContent += '...';
            break;
          }
        }
      }
    }
    return (
      <>
        <Dialog onClose={onCloseDialog} open={state}>
          <ModalRemoveContent
            headerTitle="Remove Widget"
            width={712}
            widgetName={title}
            onRemove={removeWidgetHandler}
            onClose={onCloseDialog}
          />
        </Dialog>

        {isTrendWidget && (
          <Dialog onClose={onCloseTrendDialog} open={isTrendDialogOpen}>
            <TrendModalContent
              title={title}
              options={options}
              series={series}
              onClose={onCloseTrendDialog}
              typeChart={typeChart}
              unit={unit}
              noDataAvailable={noDataAvailable}
            />
          </Dialog>
        )}

        <Box
          ref={ref}
          sx={card(
            width!,
            height!,
            isDesktop,
            isTrendWidget,
            isPumpWidget,
            isControllerWidget,
            isValveWidget,
            isLiveDataWidget,
            isWetWell,
            isLiftStationWidget,
            isAssetController
          )}
          data-sm={testId}
        >
          <Box>
            <Box sx={cardHeader}>
              <Tooltip title={title}>
                <Typography variant="h6" sx={cardTitle} className="widget-wrapper-title">
                  {isPumpWidget && title.length > 20 ? `${title.substring(0, 20)}...` : title}
                </Typography>
              </Tooltip>

              <Box sx={wrapperIconsBox}>
                {isPumpWidget && isPumpMappedToAlarmTags && (
                  <Box sx={{ marginTop: '0.2rem', display: 'flex' }}>
                    <HandlePriority isActive={isAlarmForPumpActive} />
                  </Box>
                )}

                {isEditMode && (
                  <Box sx={{ height: '20px', marginLeft: '1rem' }}>
                    <EditWidgetIcon fill={greyColor} />
                  </Box>
                )}

                <Box sx={cardContext}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isTrendWidget && isTablet && (
                      <Box
                        onClick={onTrendDialogToggle}
                        sx={{ margin: '0.3rem 0.5rem 0 1rem', cursor: 'pointer' }}
                      >
                        <ExpandIcon fill={greyColor} />
                      </Box>
                    )}

                    {getCurrentRole(customerId, siteId) !== AccessRoles.SITE_EMPLOYEE && (
                      <Box>
                        <IconButton
                          color="default"
                          data-sm="context-menu-button"
                          onClick={(event) => addHtmlElement(event.currentTarget)}
                        >
                          <MoreVerticalFillIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <MenuPopover open={htmlElement} onClose={removeHtmlElement} arrow="right-top">
                    {(isLiveDataWidget || isLiftStationWidget || isAssetController) && (
                      <MenuPopoverItem
                        icon={<EditIcon width={10} />}
                        description="Edit"
                        handleClick={onEditHandler}
                        testId="edit-button"
                      />
                    )}
                    <MenuPopoverItem
                      icon={<CloseIcon width={10} fill={redColor} />}
                      description="Remove"
                      handleClick={onRemoveHandler}
                      testId="remove-button"
                      color={redColor}
                    />
                  </MenuPopover>
                </Box>
              </Box>
            </Box>
            {timestamp && (
              <Box sx={{ marginBottom: '1rem' }}>
                <LastUpdatedBox timestamp={timestamp} />
              </Box>
            )}
          </Box>
          {isDataReady ? (
            children
          ) : (
            <Box sx={spinnerBox}>
              <CustomWaterLoading />
            </Box>
          )}
        </Box>
      </>
    );
  }
);
